/**
 * Cost Master — Unified page for Material / Labour / Equipment masters.
 *
 * Reusable shell powered by /api/cost-masters/{kind} with per-kind tweaks for
 * extra columns (output_per_day for labour, hourly/monthly rate for equipment).
 *
 * Features:
 *   • Filter chips per category + search + location filter (shows effective_rate_at_location)
 *   • Stats: total / categories / locations / auto-refresh count
 *   • Create / Edit modal with multi-location rate editor + supplier list
 *   • Inline auto_refresh toggle
 *   • Bulk Excel/CSV import
 *   • Price history drawer with sparkline of rate changes
 *   • Force "Refresh all rate library items" button
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';
import {
  Plus, Search, Edit2, Trash2, X, MapPin, Loader2, Upload, RefreshCw, Download,
  TrendingUp, History, Save, Building2, Hammer, Wrench, ChevronRight, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const KIND_META = {
  materials: {
    title: 'Material Master',
    overline: 'Cost Library · Materials',
    icon: Building2,
    desc: 'Single source of truth for material rates. Update once — Rate Library refreshes automatically.',
    extraCols: [],
  },
  labour: {
    title: 'Labour Master',
    overline: 'Cost Library · Labour',
    icon: Hammer,
    desc: 'Skilled and unskilled wage rates + productivity constants used in Rate Analysis.',
    extraCols: [
      { key: 'output_per_day', label: 'Output/Day' },
      { key: 'daily_rate', label: 'Daily Rate' },
    ],
  },
  equipment: {
    title: 'Equipment Master',
    overline: 'Cost Library · Equipment',
    icon: Wrench,
    desc: 'Hourly, daily and monthly rates for cranes, mixers, gensets etc. Includes fuel cost per hour.',
    extraCols: [
      { key: 'hourly_rate', label: 'Hourly' },
      { key: 'monthly_rate', label: 'Monthly' },
      { key: 'fuel_per_hour', label: 'Fuel/hr' },
    ],
  },
};

import { formatDateTime as _fmtDateTime } from '../../lib/format';
const fmtINR = (v) => v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const emptyForm = {
  code: '', name: '', category: '', unit: '', base_rate: '',
  base_location: 'Mumbai', auto_refresh: true, notes: '',
  location_rates: [], suppliers: [],
  output_per_day: '', daily_rate: '',
  hourly_rate: '', monthly_rate: '', fuel_per_hour: '',
};

export default function CostMaster() {
  const { kind } = useParams();
  const navigate = useNavigate();
  const meta = KIND_META[kind] || KIND_META.materials;
  const fileRef = useRef(null);

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState({ materials: [], labour: [], equipment: [] });
  const [activeCategory, setActiveCategory] = useState('all');
  const [locationFilter, setLocationFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [historyId, setHistoryId] = useState(null);
  const [history, setHistory] = useState([]);
  const [importing, setImporting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadAll = async () => {
    try {
      const params = new URLSearchParams();
      if (locationFilter) params.set('location', locationFilter);
      const [list, cats] = await Promise.all([
        apiGet(`/cost-masters/${kind}?${params.toString()}`),
        apiGet('/cost-masters/categories'),
      ]);
      setItems(list.items || []);
      setCategories(cats.categories || {});
    } catch (e) { console.error('Failed to load cost masters:', e); }
  };

  useEffect(() => { setActiveCategory('all'); setSearch(''); loadAll(); /* eslint-disable-next-line */ }, [kind, locationFilter]);

  const catList = categories[kind] || [];

  const filtered = useMemo(() => {
    let list = items;
    if (activeCategory !== 'all') list = list.filter((i) => i.category === activeCategory);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((i) =>
        (i.name || '').toLowerCase().includes(q) ||
        (i.code || '').toLowerCase().includes(q) ||
        (i.notes || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [items, activeCategory, search]);

  const stats = useMemo(() => {
    const cats = new Set(items.map((i) => i.category));
    const locs = new Set();
    items.forEach((i) => {
      if (i.base_location) locs.add(i.base_location);
      (i.location_rates || []).forEach((lr) => lr.location && locs.add(lr.location));
    });
    return {
      total: items.length,
      categories: cats.size,
      locations: locs.size,
      autoRefresh: items.filter((i) => i.auto_refresh).length,
    };
  }, [items]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, category: catList[0] || '' });
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      ...emptyForm,
      code: item.code || '', name: item.name || '', category: item.category || '',
      unit: item.unit || '', base_rate: item.base_rate ?? '',
      base_location: item.base_location || 'Mumbai',
      auto_refresh: item.auto_refresh !== false,
      notes: item.notes || '',
      location_rates: (item.location_rates || []).map((lr) => ({ location: lr.location, rate: lr.rate })),
      suppliers: (item.suppliers || []).map((s) => ({ name: s.name, contact: s.contact || '', rate: s.rate || '' })),
      output_per_day: item.output_per_day ?? '',
      daily_rate: item.daily_rate ?? '',
      hourly_rate: item.hourly_rate ?? '',
      monthly_rate: item.monthly_rate ?? '',
      fuel_per_hour: item.fuel_per_hour ?? '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name required'); return; }
    if (form.base_rate === '' || isNaN(Number(form.base_rate))) { toast.error('Base rate required'); return; }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim() || null,
        name: form.name.trim(),
        category: form.category || (catList[0] || 'Other'),
        unit: form.unit.trim() || 'Unit',
        base_rate: Number(form.base_rate),
        base_location: form.base_location.trim() || 'Mumbai',
        auto_refresh: form.auto_refresh,
        notes: form.notes.trim() || null,
        location_rates: form.location_rates
          .filter((lr) => lr.location.trim() && lr.rate !== '')
          .map((lr) => ({ location: lr.location.trim(), rate: Number(lr.rate) })),
        suppliers: form.suppliers
          .filter((s) => s.name.trim())
          .map((s) => ({ name: s.name.trim(), contact: s.contact || null, rate: s.rate === '' ? null : Number(s.rate) })),
      };
      if (kind === 'labour') {
        if (form.output_per_day !== '') payload.output_per_day = Number(form.output_per_day);
        if (form.daily_rate !== '') payload.daily_rate = Number(form.daily_rate);
      }
      if (kind === 'equipment') {
        if (form.hourly_rate !== '') payload.hourly_rate = Number(form.hourly_rate);
        if (form.monthly_rate !== '') payload.monthly_rate = Number(form.monthly_rate);
        if (form.fuel_per_hour !== '') payload.fuel_per_hour = Number(form.fuel_per_hour);
      }
      if (editing) {
        await apiPut(`/cost-masters/${kind}/${editing.id}`, payload);
        toast.success('Master updated · downstream Rate Library items refreshed');
      } else {
        await apiPost(`/cost-masters/${kind}`, payload);
        toast.success('Master created');
      }
      setShowForm(false);
      setEditing(null);
      setForm(emptyForm);
      loadAll();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Remove "${item.name}"?`)) return;
    try {
      await apiDel(`/cost-masters/${kind}/${item.id}`);
      toast.success('Removed');
      loadAll();
    } catch { toast.error('Delete failed'); }
  };

  const handleHistory = async (item) => {
    setHistoryId(item.id);
    setHistory(null);
    try {
      const h = await apiGet(`/cost-masters/${kind}/${item.id}/history`);
      setHistory(h.history || []);
    } catch { toast.error('Failed to load history'); }
  };

  const handleImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/cost-masters/${kind}/bulk-import`;
      const resp = await fetch(url, { method: 'POST', body: fd });
      if (!resp.ok) throw new Error((await resp.json())?.detail || 'Import failed');
      const out = await resp.json();
      toast.success(`Import done · ${out.created} created · ${out.updated} updated · ${out.skipped} skipped`);
      if (out.errors?.length) console.warn('Import errors:', out.errors);
      loadAll();
    } catch (err) { toast.error(err.message || 'Import failed'); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleRefreshAll = async () => {
    if (!window.confirm('Refresh ALL auto-refresh Rate Library items against current master rates?')) return;
    setRefreshing(true);
    try {
      const out = await apiPost('/cost-masters/cascade-refresh-all', {});
      toast.success(`Refreshed ${out.refreshed_items} rate library items`);
    } catch { toast.error('Refresh failed'); }
    finally { setRefreshing(false); }
  };

  const addLocationRate = () => setForm((f) => ({ ...f, location_rates: [...f.location_rates, { location: '', rate: '' }] }));
  const updateLocationRate = (idx, field, value) => setForm((f) => ({
    ...f, location_rates: f.location_rates.map((lr, i) => i === idx ? { ...lr, [field]: value } : lr),
  }));
  const removeLocationRate = (idx) => setForm((f) => ({ ...f, location_rates: f.location_rates.filter((_, i) => i !== idx) }));

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">{meta.overline}</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-3">
            <meta.icon size={28} className="text-[#3b4872]" /> {meta.title}
          </h1>
          <p className="text-sm text-slate-600 mt-1">{meta.desc}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Kind switcher */}
          <div className="flex border border-slate-300 rounded-sm overflow-hidden">
            {Object.keys(KIND_META).map((k) => (
              <button key={k} type="button" onClick={() => navigate(`/app/estimation/master/${k}`)}
                className={`px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider ${k === kind ? 'bg-[#3b4872] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                data-testid={`cm-switch-${k}`}>{KIND_META[k].title.replace(' Master', '')}</button>
            ))}
          </div>
          <button onClick={handleRefreshAll} disabled={refreshing} className="btn-secondary flex items-center gap-2 text-xs" data-testid="cm-refresh-all">
            {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh Library
          </button>
          <label className={`btn-secondary flex items-center gap-2 text-xs cursor-pointer ${importing ? 'opacity-50 pointer-events-none' : ''}`} data-testid="cm-import-label">
            {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Import
            <input type="file" accept=".csv,.xlsx,.xls" onChange={handleImport} ref={fileRef} hidden data-testid="cm-import-input" />
          </label>
          <button
            onClick={async () => {
              try {
                const token = localStorage.getItem('access_token');
                const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cost-masters/${kind}/template.xlsx`, {
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = `cost-master-${kind}-template.xlsx`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success('Template downloaded');
              } catch (e) { toast.error('Could not download template: ' + (e.message || '')); }
            }}
            className="btn-secondary flex items-center gap-2 text-xs"
            data-testid="cm-template-btn"
            title="Download sample import template"
          >
            <Download size={14} /> Template
          </button>
          <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="cm-add-btn">
            <Plus size={16} /> Add {kind === 'materials' ? 'Material' : kind === 'labour' ? 'Labour' : 'Equipment'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="cm-stats">
        <Stat label="Total Items" value={stats.total} />
        <Stat label="Categories" value={stats.categories} />
        <Stat label="Locations" value={stats.locations} />
        <Stat label="Auto-Refresh" value={`${stats.autoRefresh} / ${stats.total}`} />
      </div>

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <button onClick={() => setActiveCategory('all')} className={chip(activeCategory === 'all')} data-testid="cm-cat-all">All ({items.length})</button>
        {catList.map((c) => (
          <button key={c} onClick={() => setActiveCategory(c)} className={chip(activeCategory === c)} data-testid={`cm-cat-${c.replace(/\s+/g, '-').toLowerCase()}`}>
            {c} ({items.filter((i) => i.category === c).length})
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <MapPin size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}
              placeholder="Filter location e.g. Pune"
              className="input pl-7 text-xs w-44" data-testid="cm-location-filter" />
          </div>
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, code…" className="input pl-7 text-xs w-60" data-testid="cm-search" />
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="cm-list">
        <table className="w-full dense-table">
          <thead>
            <tr>
              <th>Code · Name</th>
              <th>Category</th>
              <th>Unit</th>
              <th className="text-right">Base Rate</th>
              {meta.extraCols.map((c) => <th key={c.key} className="text-right">{c.label}</th>)}
              <th>Locations</th>
              <th>Auto</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => (
              <tr key={it.id} data-testid={`cm-row-${it.id}`}>
                <td>
                  <div className="font-display font-semibold text-[#0F172A]">{it.name}</div>
                  <div className="text-[11px] text-slate-500">{it.code}</div>
                </td>
                <td className="text-xs">{it.category}</td>
                <td className="text-xs">{it.unit}</td>
                <td className="text-right font-display font-bold">
                  {fmtINR(locationFilter ? (it.effective_rate_at_location ?? it.base_rate) : it.base_rate)}
                  {locationFilter && it.effective_rate_at_location != null && it.effective_rate_at_location !== it.base_rate && (
                    <div className="text-[10px] text-emerald-700">@ {locationFilter}</div>
                  )}
                </td>
                {meta.extraCols.map((c) => <td key={c.key} className="text-right text-xs">{it[c.key] != null ? fmtINR(it[c.key]) : '—'}</td>)}
                <td className="text-xs">
                  <div className="flex flex-wrap gap-1 items-center">
                    <span className="tag bg-slate-100 text-slate-700 text-[10px]" title="Base location">{it.base_location}</span>
                    {(it.location_rates || []).map((lr, idx) => (
                      <span
                        key={`${it.id}-${idx}`}
                        className="tag bg-indigo-50 text-[#3b4872] text-[10px]"
                        title={`Rate: ${fmtINR(lr.rate)}`}
                        data-testid={`cm-loc-${it.id}-${idx}`}
                      >
                        {lr.location} · {fmtINR(lr.rate)}
                      </span>
                    ))}
                  </div>
                </td>
                <td>
                  {it.auto_refresh ? <span className="tag bg-emerald-100 text-emerald-700 text-[10px]">ON</span> : <span className="tag bg-slate-100 text-slate-600 text-[10px]">OFF</span>}
                </td>
                <td>
                  <div className="flex items-center gap-1">
                    <button onClick={() => handleHistory(it)} className="text-slate-500 hover:text-[#3b4872] px-1" title="Price History" data-testid={`cm-history-${it.id}`}><History size={14} /></button>
                    <button onClick={() => openEdit(it)} className="text-slate-500 hover:text-[#3b4872] px-1" title="Edit" data-testid={`cm-edit-${it.id}`}><Edit2 size={14} /></button>
                    <button onClick={() => handleDelete(it)} className="text-red-600 hover:text-red-700 px-1" title="Delete" data-testid={`cm-del-${it.id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-10 text-center text-sm text-slate-500">
            {items.length === 0 ? `No ${kind} entries yet. Click Add or Import to start.` : 'No items match the current filter.'}
          </div>
        )}
      </div>

      {/* History Drawer */}
      {historyId && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setHistoryId(null); setHistory([]); }} />
          <div className="relative w-full max-w-md bg-white shadow-2xl h-full overflow-y-auto" data-testid="cm-history-drawer">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-center justify-between">
              <div>
                <div className="font-display text-xl font-extrabold">Price History</div>
                <div className="text-xs text-slate-500">{items.find((i) => i.id === historyId)?.name}</div>
              </div>
              <button onClick={() => { setHistoryId(null); setHistory([]); }} className="text-slate-500 hover:text-slate-700"><X size={20} /></button>
            </div>
            <div className="p-5">
              {history === null ? (
                <div className="text-center text-sm text-slate-500"><Loader2 size={20} className="inline animate-spin" /></div>
              ) : history.length === 0 ? (
                <div className="text-sm text-slate-500 italic">No price changes recorded yet.</div>
              ) : (
                <ol className="space-y-3">
                  {history.map((h, i) => (
                    <li key={h.id || i} className="relative pl-4 border-l-2 border-slate-200" data-testid={`cm-history-row-${i}`}>
                      <div className="absolute -left-[5px] top-1 w-2 h-2 rounded-full bg-[#3b4872]" />
                      <div className="text-xs text-slate-500">{_fmtDateTime(h.changed_at)}</div>
                      <div className="font-display font-semibold text-sm">
                        {h.field === 'created' ? 'Created' : h.field === 'base_rate' ? 'Base rate change' : `Location rate · ${h.location}`}
                      </div>
                      <div className="text-sm">
                        {h.from != null && <span className="line-through text-slate-400">{fmtINR(h.from)}</span>}
                        {h.from != null && ' → '}
                        <span className="font-bold text-[#0F172A]">{fmtINR(h.to)}</span>
                        {h.source === 'bulk_import' && <span className="ml-2 tag bg-indigo-50 text-[#3b4872] text-[9px]">via import</span>}
                      </div>
                      <div className="text-[11px] text-slate-500">by {h.changed_by}</div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between z-10">
              <h2 className="font-display text-xl font-bold">{editing ? `Edit ${meta.title}` : `Add ${meta.title}`}</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-500 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Code</label>
                  <input type="text" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="input" placeholder="auto-generated if left blank" />
                </div>
                <div>
                  <label className="label">Name *</label>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" required data-testid="cm-form-name" placeholder={kind === 'materials' ? 'e.g. Cement OPC 53' : kind === 'labour' ? 'e.g. Mason' : 'e.g. Concrete Mixer 1 cum'} />
                </div>
                <div>
                  <label className="label">Category *</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input" data-testid="cm-form-category">
                    {catList.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Unit *</label>
                  <input type="text" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="input" required placeholder={kind === 'materials' ? 'Bag / Kg / Cum' : kind === 'labour' ? 'Day / Hour' : 'Hour / Day'} />
                </div>
                <div>
                  <label className="label">Base Rate (₹) *</label>
                  <input type="number" step="0.01" value={form.base_rate} onChange={(e) => setForm({ ...form, base_rate: e.target.value })} className="input" required data-testid="cm-form-base-rate" />
                </div>
                <div>
                  <label className="label">Base Location *</label>
                  <input type="text" value={form.base_location} onChange={(e) => setForm({ ...form, base_location: e.target.value })} className="input" placeholder="e.g. Mumbai" />
                </div>

                {kind === 'labour' && (
                  <>
                    <div>
                      <label className="label">Output Per Day <span className="text-[10px] text-slate-500">(productivity)</span></label>
                      <input type="number" step="0.01" value={form.output_per_day} onChange={(e) => setForm({ ...form, output_per_day: e.target.value })} className="input" />
                    </div>
                    <div>
                      <label className="label">Daily Wage (₹)</label>
                      <input type="number" step="0.01" value={form.daily_rate} onChange={(e) => setForm({ ...form, daily_rate: e.target.value })} className="input" />
                    </div>
                  </>
                )}
                {kind === 'equipment' && (
                  <>
                    <div>
                      <label className="label">Hourly Rate (₹)</label>
                      <input type="number" step="0.01" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="input" />
                    </div>
                    <div>
                      <label className="label">Monthly Rate (₹)</label>
                      <input type="number" step="0.01" value={form.monthly_rate} onChange={(e) => setForm({ ...form, monthly_rate: e.target.value })} className="input" />
                    </div>
                    <div>
                      <label className="label">Fuel Cost / Hour (₹)</label>
                      <input type="number" step="0.01" value={form.fuel_per_hour} onChange={(e) => setForm({ ...form, fuel_per_hour: e.target.value })} className="input" />
                    </div>
                  </>
                )}

                <div className="col-span-2">
                  <label className="label">Notes</label>
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" rows={2} />
                </div>

                <div className="col-span-2 flex items-center gap-2">
                  <input type="checkbox" id="cm-form-auto" checked={form.auto_refresh} onChange={(e) => setForm({ ...form, auto_refresh: e.target.checked })} className="w-4 h-4 accent-[#3b4872]" data-testid="cm-form-auto" />
                  <label htmlFor="cm-form-auto" className="text-sm">Auto-refresh dependent Rate Library items when rate changes</label>
                </div>
              </div>

              {/* Location-wise rate editor */}
              <div className="border-t border-slate-200 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display font-bold text-sm flex items-center gap-2"><MapPin size={14} /> Location-wise Rates <span className="text-[10px] text-slate-500 font-normal">(overrides base rate)</span></div>
                  <button type="button" onClick={addLocationRate} className="text-xs text-[#3b4872] hover:underline flex items-center gap-1" data-testid="cm-add-loc-rate">
                    <Plus size={12} /> Add Location
                  </button>
                </div>
                {form.location_rates.length === 0 ? (
                  <div className="text-xs text-slate-400 italic">No location overrides. Base rate applies everywhere.</div>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-[1fr_140px_28px] gap-2 px-1 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">
                      <div>Location / City</div>
                      <div className="text-right">Rate (₹)</div>
                      <div></div>
                    </div>
                    {form.location_rates.map((lr, i) => (
                      <div key={i} className="grid grid-cols-[1fr_140px_28px] gap-2 items-center" data-testid={`cm-loc-rate-${i}`}>
                        <input
                          type="text"
                          value={lr.location || ''}
                          onChange={(e) => updateLocationRate(i, 'location', e.target.value)}
                          className="input text-sm"
                          placeholder="City e.g. Pune"
                          data-testid={`cm-loc-rate-city-${i}`}
                        />
                        <input
                          type="number"
                          step="0.01"
                          value={lr.rate ?? ''}
                          onChange={(e) => updateLocationRate(i, 'rate', e.target.value)}
                          className="input text-sm text-right"
                          placeholder="Rate"
                          data-testid={`cm-loc-rate-amt-${i}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeLocationRate(i)}
                          className="text-red-600 hover:text-red-700 flex items-center justify-center w-7 h-7 hover:bg-red-50 rounded-sm"
                          title="Remove"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 justify-end pt-2 border-t border-slate-200">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving} data-testid="cm-form-submit">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {editing ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const chip = (active) => `px-3 py-1.5 rounded-sm text-xs font-display font-bold uppercase tracking-wider border ${active ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-700 border-slate-300 hover:border-[#3b4872]'}`;

const Stat = ({ label, value }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-4">
    <div className="text-[10px] uppercase tracking-wider font-display font-bold text-slate-500">{label}</div>
    <div className="font-display text-2xl font-extrabold text-[#0F172A] mt-1">{value}</div>
  </div>
);
